// src/app/page.tsx
'use client';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Star, Zap, Trophy, Users, TrendingUp, Sparkles, Target, Award, Menu, X, GraduationCap, Rocket, BookOpen, Brain, Flame, AlertTriangle, Clock } from 'lucide-react';
import Image from 'next/image';
import { useState, useEffect, useRef } from 'react';
import { Logo } from '@/components/ui/logo';
import { LazyLottie } from '@/components/LazyLottie';
import dynamic from 'next/dynamic';

const HomeNavAuth = dynamic(() => import('@/components/HomeNavAuth'), { ssr: false, loading: () => null });

export default function Home() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [whoIsThisForProgress, setWhoIsThisForProgress] = useState(0);
  const howItWorksRef = useRef<HTMLElement>(null);
  const whoIsThisForRef = useRef<HTMLElement>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20);

          // Calculate scroll progress for "How It Works" section
          if (howItWorksRef.current) {
            const rect = howItWorksRef.current.getBoundingClientRect();
            const sectionHeight = howItWorksRef.current.offsetHeight;
            const viewportHeight = window.innerHeight;

            // Calculate progress from 0 to 1 as user scrolls through the section
            const scrolled = -rect.top;
            const total = sectionHeight - viewportHeight;
            const progress = Math.max(0, Math.min(1, scrolled / total));

            // Only update if progress changed significantly (reduces re-renders)
            setScrollProgress((prev) => {
              const diff = Math.abs(prev - progress);
              return diff > 0.01 ? progress : prev;
            });
          }

          // Calculate scroll progress for "Who Is This For?" section
          if (whoIsThisForRef.current) {
            const rect = whoIsThisForRef.current.getBoundingClientRect();
            const sectionHeight = whoIsThisForRef.current.offsetHeight;
            const viewportHeight = window.innerHeight;

            const scrolled = -rect.top;
            const total = sectionHeight - viewportHeight;
            const progress = Math.max(0, Math.min(1, scrolled / total));

            setWhoIsThisForProgress((prev) => {
              const diff = Math.abs(prev - progress);
              return diff > 0.01 ? progress : prev;
            });
          }

          ticking = false;
        });

        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  // Determine active step based on scroll progress
  const activeStep = scrollProgress < 0.33 ? 1 : scrollProgress < 0.66 ? 2 : 3;

  // Determine active persona based on scroll progress (4 personas)
  const activePersona = whoIsThisForProgress < 0.25 ? 1 : whoIsThisForProgress < 0.5 ? 2 : whoIsThisForProgress < 0.75 ? 3 : 4;

  // Dynamic content for each step
  const stepVisuals = {
    1: { emoji: '🎯', color: 'green' },
    2: { emoji: '🚀', color: 'blue' },
    3: { emoji: '🎓', color: 'purple' }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Glassmorphic Header - Fixed */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300">
        <div className="flex justify-center pt-4 px-4">
          <nav
            className={`
              flex h-16 w-full max-w-6xl items-center justify-between
              rounded-full border backdrop-blur-2xl
              px-6 transition-all duration-300
              ${isScrolled
                ? 'bg-white/90 border-white/50 shadow-2xl shadow-green-500/10'
                : 'bg-white/70 border-white/40 shadow-xl'
              }
            `}
          >
            {/* Logo */}
            <Link href="/" className="flex items-center gap-3">
              <Logo
                size="lg"
                showText={false}
                className="sm:hidden"
              />
              <Logo
                size="lg"
                textClassName="text-xl bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent hidden sm:block"
                className="hidden sm:flex"
              />
            </Link>

            {/* Nav auth + mobile menu (deferred — loads after hydration) */}
            <HomeNavAuth isMobileMenuOpen={isMobileMenuOpen} />

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              className="md:hidden p-2 rounded-full hover:bg-white/60 transition-all"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6 text-gray-700" />
              ) : (
                <Menu className="h-6 w-6 text-gray-700" />
              )}
            </button>
          </nav>
        </div>

      </header>

      <main>

      {/* Hero Section - Revised */}
      <section className="relative overflow-hidden bg-gradient-to-b from-green-50 via-blue-50 to-white pt-20">
        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 py-6 sm:py-10 lg:py-16">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Mobile Mascot - Shown first on mobile/tablet (before text) */}
            <div className="lg:hidden flex justify-center order-first">
              <div className="relative w-48 h-48 sm:w-64 sm:h-64">
                <div className="absolute inset-0 rounded-3xl border-4 border-white shadow-xl overflow-hidden bg-gradient-to-br from-purple-50 via-white to-orange-50">
                  <div className="absolute inset-0 flex items-center justify-center p-4" role="img" aria-label="InnoG - Your AI Learning Companion">
                    <LazyLottie
                      src="/animations/animation.json"
                      eager
                      loop={true}
                      autoplay={true}
                      className="w-full h-full"
                      style={{ mixBlendMode: 'multiply' }}
                    />
                  </div>
                </div>
                {/* Mobile floating badges */}
                <div className="absolute -left-2 -bottom-2 bg-white rounded-xl shadow-lg p-2 border-2 border-orange-200">
                  <div className="flex items-center gap-2">
                    <Flame className="h-5 w-5 text-orange-500 fill-current" />
                    <span className="font-bold text-gray-900 text-sm">14 day streak</span>
                  </div>
                </div>
                <div className="absolute -right-2 -top-2 bg-white rounded-xl shadow-lg p-2 border-2 border-green-200">
                  <div className="flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-green-600" />
                    <span className="font-bold text-gray-900 text-sm">Level Up!</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Left Column - Text Content */}
            <div className="space-y-5 sm:space-y-6 lg:space-y-8 text-center lg:text-left">
              {/* Social Proof Badges */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center lg:justify-start animate-fadeIn" style={{ animationDelay: '0ms', animationFillMode: 'both' }}>
                <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 sm:px-4 py-2 rounded-full font-semibold text-xs sm:text-sm border-2 border-green-200">
                  <GraduationCap className="h-4 w-4 flex-shrink-0" />
                  <span>Created by Ivy League educators</span>
                </div>
                <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 sm:px-4 py-2 rounded-full font-semibold text-xs sm:text-sm border-2 border-blue-200">
                  <Users className="h-4 w-4 flex-shrink-0" />
                  <span>First choice of toppers across the world</span>
                </div>
              </div>

              {/* Main Headline */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-gray-900 leading-tight animate-fadeIn" style={{ animationDelay: '120ms', animationFillMode: 'both' }}>
                The all-in-one platform to{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-blue-600">
                  get YOU into top universities.
                </span>
              </h1>

              {/* Sub heading */}
              <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-gray-600 leading-relaxed animate-fadeIn" style={{ animationDelay: '240ms', animationFillMode: 'both' }}>
                For IB, IGCSE & A-Level students - <strong>AI-powered learning</strong>, <strong>real mentor oversight</strong>, and a standout profile. All in one place.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start animate-fadeIn" style={{ animationDelay: '360ms', animationFillMode: 'both' }}>
                <Link href="/sign-up" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto rounded-xl sm:rounded-2xl bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold text-base sm:text-lg px-8 sm:px-10 py-4 sm:py-6 shadow-xl hover:shadow-2xl transition-all hover:scale-105"
                  >
                    Start Your Free Trial →
                  </Button>
                </Link>
                <a href="#how-it-works" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto rounded-xl sm:rounded-2xl border-2 border-gray-200 bg-white text-gray-700 font-bold text-base sm:text-lg px-8 sm:px-10 py-4 sm:py-6 hover:bg-gray-50 hover:border-gray-300 transition-all"
                  >
                    Watch How It Works
                  </Button>
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 sm:gap-6 pt-2 sm:pt-4 animate-fadeIn" style={{ animationDelay: '480ms', animationFillMode: 'both' }}>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 sm:h-5 sm:w-5 fill-amber-400 text-amber-400" />
                  ))}
                  <span className="text-xs sm:text-sm font-semibold text-gray-700 ml-1 sm:ml-2">4.9/5 from students</span>
                </div>
                <div className="text-xs sm:text-sm font-semibold text-gray-700">
                  ✓ Zero boredom guaranteed
                </div>
              </div>
            </div>

            {/* Right Column - Visual with InnoG (Desktop only) */}
            <div className="relative hidden lg:block animate-fadeIn" style={{ animationDelay: '200ms', animationFillMode: 'both' }}>
              <div className="relative">
                {/* Main Visual Card */}
                <div className="relative w-full aspect-square max-w-md mx-auto rounded-3xl border-4 border-white shadow-2xl overflow-hidden bg-gradient-to-br from-purple-50 via-white to-orange-50">
                  {/* Mascot Animation */}
                  <div className="absolute inset-0 flex items-center justify-center p-8" role="img" aria-label="InnoG - Your AI Learning Companion">
                    <LazyLottie
                      src="/animations/animation.json"
                      eager
                      loop={true}
                      autoplay={true}
                      className="w-full h-full"
                      style={{ mixBlendMode: 'multiply' }}
                    />
                  </div>

                </div>

                {/* Floating Cards */}
                <div className="absolute -left-8 top-1/4 bg-white rounded-2xl shadow-xl p-4 border-2 border-orange-200 transform rotate-[-5deg] transition-all hover:rotate-0 hover:scale-105">
                  <div className="flex items-center gap-3">
                    <Flame className="h-8 w-8 text-orange-500 fill-current" />
                    <div>
                      <div className="font-bold text-gray-900">14 day streak</div>
                      <div className="text-sm text-gray-500">Keep going!</div>
                    </div>
                  </div>
                </div>

                <div className="absolute -right-8 top-1/2 bg-white rounded-2xl shadow-xl p-4 border-2 border-green-200 transform rotate-[5deg] transition-all hover:rotate-0 hover:scale-105">
                  <div className="flex items-center gap-3">
                    <Trophy className="h-8 w-8 text-green-600" />
                    <div>
                      <div className="font-bold text-gray-900">Level Up!</div>
                      <div className="text-sm text-gray-500">Module complete</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ivy League Universities - Static Row */}
      <section className="py-8 sm:py-10 bg-gray-50 border-y border-gray-200">
        <div className="container mx-auto px-4">
          <p className="text-center text-sm sm:text-base font-semibold text-gray-600 mb-6">
            Our students have gone on to:
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 md:gap-12 lg:gap-16">
            <Image src="/logos/harvard.png" alt="Harvard University" width={120} height={40} className="h-8 sm:h-10 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100" />
            <Image src="/logos/yale.png" alt="Yale University" width={120} height={40} className="h-8 sm:h-10 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100" />
            <Image src="/logos/princeton.png" alt="Princeton University" width={120} height={40} className="h-8 sm:h-10 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100" />
            <Image src="/logos/columbia.png" alt="Columbia University" width={120} height={40} className="h-8 sm:h-10 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100" />
            <Image src="/logos/upenn.png" alt="University of Pennsylvania" width={120} height={40} className="h-8 sm:h-10 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100" />
            <Image src="/logos/brown.png" alt="Brown University" width={120} height={40} className="h-8 sm:h-10 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100" />
            <Image src="/logos/dartmouth.png" alt="Dartmouth College" width={120} height={40} className="h-8 sm:h-10 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100" />
            <Image src="/logos/cornell.png" alt="Cornell University" width={120} height={40} className="h-8 sm:h-10 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100" />
          </div>
        </div>
      </section>

      {/* Stats Section - Visual Cards with Mascots */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-6xl mx-auto">
            {/* Students from Countries */}
            <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-gray-200 hover:shadow-lg hover:border-green-200 transition-all group text-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-3 sm:mb-4 rounded-xl bg-green-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Users className="w-6 h-6 sm:w-7 sm:h-7 text-green-600" />
              </div>
              <div className="text-2xl sm:text-3xl md:text-4xl font-black mb-1 bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">15+</div>
              <div className="text-xs sm:text-sm text-gray-600 font-medium">Countries</div>
            </div>

            {/* Avg. Daily Engagement */}
            <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-gray-200 hover:shadow-lg hover:border-blue-200 transition-all group text-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-3 sm:mb-4 rounded-xl bg-blue-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Clock className="w-6 h-6 sm:w-7 sm:h-7 text-blue-600" />
              </div>
              <div className="text-2xl sm:text-3xl md:text-4xl font-black mb-1 bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">45 min</div>
              <div className="text-xs sm:text-sm text-gray-600 font-medium">Avg. Daily Engagement</div>
            </div>

            {/* 1M+ Questions */}
            <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-gray-200 hover:shadow-lg hover:border-purple-200 transition-all group text-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-3 sm:mb-4 rounded-xl bg-purple-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                <BookOpen className="w-6 h-6 sm:w-7 sm:h-7 text-purple-600" />
              </div>
              <div className="text-2xl sm:text-3xl md:text-4xl font-black mb-1 bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">1M+</div>
              <div className="text-xs sm:text-sm text-gray-600 font-medium">Practice Questions</div>
            </div>

            {/* 24/7 AI Support */}
            <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-gray-200 hover:shadow-lg hover:border-orange-200 transition-all group text-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-3 sm:mb-4 rounded-xl bg-orange-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Brain className="w-6 h-6 sm:w-7 sm:h-7 text-orange-600" />
              </div>
              <div className="text-2xl sm:text-3xl md:text-4xl font-black mb-1 bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">24/7</div>
              <div className="text-xs sm:text-sm text-gray-600 font-medium">AI Tutor Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* Who Is This For? - Mobile Version (simple linear layout) */}
      <section className="md:hidden py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-3">
              Who Is This For?
            </h2>
            <p className="text-lg text-gray-600">InnOGyan is perfect for you if...</p>
          </div>

          <div className="space-y-4 max-w-md mx-auto">
            {/* Students */}
            <div className="relative bg-white rounded-2xl p-5 border-3 border-purple-300 shadow-lg overflow-hidden">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-20 h-20 flex-shrink-0">
                  <LazyLottie src="/animations/mascot-study.json" loop={true} autoplay={true} className="w-full h-full" style={{ mixBlendMode: 'multiply' }} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-black text-gray-900 mb-1">IB/IGCSE/A Level Students</h3>
                  <p className="text-gray-600 text-sm">Ace exams with AI tutoring + mentor tracking</p>
                </div>
              </div>
              <div className="pl-2 border-l-2 border-purple-300">
                <p className="text-sm font-bold text-purple-700">→ Go from predicted 5 to 7 in IB.</p>
              </div>
              <Link href="/sign-up" className="inline-flex items-center min-h-[44px] mt-3 text-xs font-semibold text-purple-600 hover:text-purple-700 transition-colors">
                See how →
              </Link>
            </div>

            {/* Career Explorers */}
            <div className="relative bg-white rounded-2xl p-5 border-3 border-blue-300 shadow-lg overflow-hidden">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-20 h-20 flex-shrink-0">
                  <LazyLottie src="/animations/mascot-walking.json" loop={true} autoplay={true} className="w-full h-full" style={{ mixBlendMode: 'multiply' }} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-black text-gray-900 mb-1">Career Explorers</h3>
                  <p className="text-gray-600 text-sm">AI-guided self-discovery with mentor insights</p>
                </div>
              </div>
              <div className="pl-2 border-l-2 border-blue-300">
                <p className="text-sm font-bold text-blue-700">→ Discover your direction before Grade 11.</p>
              </div>
              <Link href="/sign-up" className="inline-flex items-center min-h-[44px] mt-3 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors">
                See how →
              </Link>
            </div>

            {/* High Achievers */}
            <div className="relative bg-white rounded-2xl p-5 border-3 border-orange-300 shadow-lg overflow-hidden">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-20 h-20 flex-shrink-0">
                  <LazyLottie src="/animations/mascot-tech.json" loop={true} autoplay={true} className="w-full h-full" style={{ mixBlendMode: 'multiply' }} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-black text-gray-900 mb-1">High Achievers</h3>
                  <p className="text-gray-600 text-sm">Build standout projects with AI + mentor review</p>
                </div>
              </div>
              <div className="pl-2 border-l-2 border-orange-300">
                <p className="text-sm font-bold text-orange-700">→ Build the project that gets you into your dream university.</p>
              </div>
              <Link href="/sign-up" className="inline-flex items-center min-h-[44px] mt-3 text-xs font-semibold text-orange-700 hover:text-orange-800 transition-colors">
                See how →
              </Link>
            </div>

            {/* Parents */}
            <div className="relative bg-white rounded-2xl p-5 border-3 border-green-300 shadow-lg overflow-hidden">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-20 h-20 flex-shrink-0">
                  <LazyLottie src="/animations/mascot-warm.json" loop={true} autoplay={true} className="w-full h-full" style={{ mixBlendMode: 'multiply' }} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-black text-gray-900 mb-1">Parents</h3>
                  <p className="text-gray-600 text-sm">Quality AI learning with mentor oversight</p>
                </div>
              </div>
              <div className="pl-2 border-l-2 border-green-300">
                <p className="text-sm font-bold text-green-700">→ Know exactly what your child is working on, every week.</p>
              </div>
              <Link href="/sign-up" className="inline-flex items-center min-h-[44px] mt-3 text-xs font-semibold text-green-700 hover:text-green-800 transition-colors">
                See how →
              </Link>
            </div>
          </div>

          {/* AI + Mentor Highlight - Mobile */}
          <div className="mt-8 max-w-md mx-auto">
            <div className="bg-gradient-to-r from-green-50 via-blue-50 to-purple-50 rounded-2xl p-5 border-2 border-green-200">
              <div className="text-center">
                <h3 className="text-lg font-black text-gray-900 mb-2">
                  AI + Human Mentors
                </h3>
                <p className="text-gray-600 text-sm">
                  InnoG provides 24/7 AI learning while mentors ensure you stay on track.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who Is This For? - Desktop Scrollytelling (hidden on mobile) */}
      <section ref={whoIsThisForRef} className="relative bg-white hidden md:block min-h-[300vh]">
        <div className="sticky top-0 h-screen flex items-center">
          {/* Background decoration */}
          <div className={`absolute inset-0 transition-all duration-700 ${
            activePersona === 1 ? 'bg-gradient-to-b from-purple-50/30 to-purple-50/10' :
            activePersona === 2 ? 'bg-gradient-to-b from-blue-50/30 to-blue-50/10' :
            activePersona === 3 ? 'bg-gradient-to-b from-orange-50/30 to-orange-50/10' :
            'bg-gradient-to-b from-green-50/30 to-green-50/10'
          }`} />

          <div className="container mx-auto px-4 relative z-10 py-8">
            <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              {/* Left side - Dynamic visual with mascot */}
              <div className="relative">
                <div className="text-center">
                  {/* Animated mascot */}
                  <div className="w-80 h-80 mx-auto mb-8 transition-all duration-500 transform hover:scale-110">
                    <LazyLottie
                      src={
                        activePersona === 1 ? '/animations/mascot-study.json' :
                        activePersona === 2 ? '/animations/mascot-walking.json' :
                        activePersona === 3 ? '/animations/mascot-tech.json' :
                        '/animations/mascot-warm.json'
                      }
                      loop={true}
                      autoplay={true}
                      className="w-full h-full"
                      style={{ mixBlendMode: 'multiply' }}
                    />
                  </div>
                  {/* Progress bars */}
                  <div className="space-y-3 max-w-xs mx-auto">
                    <div className={`h-2 rounded-full transition-all duration-700 ${
                      activePersona >= 1 ? 'w-full bg-gradient-to-r from-purple-400 to-purple-600' : 'w-full bg-gray-200'
                    }`}></div>
                    <div className={`mx-auto h-2 rounded-full transition-all duration-700 ${
                      activePersona >= 2 ? 'w-4/5 bg-gradient-to-r from-blue-400 to-blue-600' : 'w-4/5 bg-gray-200'
                    }`}></div>
                    <div className={`mx-auto h-2 rounded-full transition-all duration-700 ${
                      activePersona >= 3 ? 'w-3/5 bg-gradient-to-r from-orange-400 to-orange-600' : 'w-3/5 bg-gray-200'
                    }`}></div>
                    <div className={`mx-auto h-2 rounded-full transition-all duration-700 ${
                      activePersona >= 4 ? 'w-2/5 bg-gradient-to-r from-green-400 to-green-600' : 'w-2/5 bg-gray-200'
                    }`}></div>
                  </div>
                </div>
              </div>

              {/* Right side - Heading + Vertical Card Stack */}
              <div>
                <div className="text-center mb-8">
                  <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
                    Who Is This For?
                  </h2>
                  <p className="text-xl text-gray-600">InnOGyan is perfect for you if...</p>
                </div>

                {/* Card stack container */}
                <div className="relative h-[450px] w-full">
                  {/* Card 1 - IB/IGCSE/A Level Students */}
                  <div
                    className={`absolute inset-x-0 bg-white rounded-3xl p-6 border-4 border-purple-400 shadow-xl transition-all duration-700 ${
                      activePersona === 1 ? 'z-30 opacity-100 scale-100 translate-y-0' :
                      activePersona === 2 ? 'z-10 opacity-40 scale-95 translate-y-8' :
                      'z-0 opacity-0'
                    }`}
                  >
                    <h3 className="text-2xl font-black text-gray-900 mb-3">IB/IGCSE/A Level Students</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      You want to ace your exams with AI tutoring that adapts to your pace, while mentors track your progress.
                    </p>
                    <div className="pl-4 border-l-4 border-purple-400 mb-4">
                      <p className="text-base font-bold text-purple-700">→ Go from predicted 5 to 7 in IB.</p>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold">24/7 AI tutor</span>
                      <span className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold">Mentor tracking</span>
                    </div>
                    <Link href="/sign-up" className="inline-flex items-center min-h-[44px] text-base font-bold text-purple-600 hover:text-purple-700 transition-colors">
                      See how →
                    </Link>
                  </div>

                  {/* Card 2 - Career Explorers */}
                  <div
                    className={`absolute inset-x-0 bg-white rounded-3xl p-6 border-4 border-blue-400 shadow-xl transition-all duration-700 ${
                      activePersona === 2 ? 'z-30 opacity-100 scale-100 translate-y-0' :
                      activePersona === 1 ? 'z-10 opacity-40 scale-95 -translate-y-8' :
                      activePersona === 3 ? 'z-10 opacity-40 scale-95 translate-y-8' :
                      'z-0 opacity-0'
                    }`}
                  >
                    <h3 className="text-2xl font-black text-gray-900 mb-3">Career Explorers</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      You&apos;re unsure about your future career and want AI-guided self-discovery with mentor insights.
                    </p>
                    <div className="pl-4 border-l-4 border-blue-400 mb-4">
                      <p className="text-base font-bold text-blue-700">→ Discover your direction before Grade 11.</p>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">Career matching</span>
                      <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">Self-discovery</span>
                    </div>
                    <Link href="/sign-up" className="inline-flex items-center min-h-[44px] text-base font-bold text-blue-600 hover:text-blue-700 transition-colors">
                      See how →
                    </Link>
                  </div>

                  {/* Card 3 - High Achievers */}
                  <div
                    className={`absolute inset-x-0 bg-white rounded-3xl p-6 border-4 border-orange-400 shadow-xl transition-all duration-700 ${
                      activePersona === 3 ? 'z-30 opacity-100 scale-100 translate-y-0' :
                      activePersona === 2 ? 'z-10 opacity-40 scale-95 -translate-y-8' :
                      activePersona === 4 ? 'z-10 opacity-40 scale-95 translate-y-8' :
                      'z-0 opacity-0'
                    }`}
                  >
                    <h3 className="text-2xl font-black text-gray-900 mb-3">High Achievers</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      You want to build passion projects that stand out on university applications, guided by AI and reviewed by mentors.
                    </p>
                    <div className="pl-4 border-l-4 border-orange-400 mb-4">
                      <p className="text-base font-bold text-orange-700">→ Build the project that gets you into your dream university.</p>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">Project builder</span>
                      <span className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">Mentor review</span>
                    </div>
                    <Link href="/sign-up" className="inline-flex items-center min-h-[44px] text-base font-bold text-orange-700 hover:text-orange-800 transition-colors">
                      See how →
                    </Link>
                  </div>

                  {/* Card 4 - Parents */}
                  <div
                    className={`absolute inset-x-0 bg-white rounded-3xl p-6 border-4 border-green-400 shadow-xl transition-all duration-700 ${
                      activePersona === 4 ? 'z-30 opacity-100 scale-100 translate-y-0' :
                      activePersona === 3 ? 'z-10 opacity-40 scale-95 -translate-y-8' :
                      'z-0 opacity-0'
                    }`}
                  >
                    <h3 className="text-2xl font-black text-gray-900 mb-3">Parents</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      You want quality AI-assisted learning for your child, with the peace of mind that real mentors are keeping track.
                    </p>
                    <div className="pl-4 border-l-4 border-green-400 mb-4">
                      <p className="text-base font-bold text-green-700">→ Know exactly what your child is working on, every week.</p>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-semibold">Safe AI learning</span>
                      <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-semibold">Mentor oversight</span>
                    </div>
                    <Link href="/sign-up" className="inline-flex items-center min-h-[44px] text-base font-bold text-green-700 hover:text-green-800 transition-colors">
                      See how →
                    </Link>
                  </div>
                </div>

                <p className="mt-6 text-center text-sm text-gray-500 italic">
                  {activePersona === 1 && '👇 Scroll to see more personas'}
                  {activePersona === 2 && '👇 Keep scrolling...'}
                  {activePersona === 3 && '👇 Almost there...'}
                  {activePersona === 4 && '✨ AI + Human Mentors = Best of Both Worlds'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll spacers for each persona */}
        <div className="h-screen"></div>
        <div className="h-screen"></div>
        <div className="h-screen"></div>
      </section>

      {/* Three Pathways - Simplified with Mascots */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-3 sm:mb-4">
              Three Pathways to Success
            </h2>
            <p className="text-lg sm:text-xl text-gray-600">Choose your journey - or explore them all</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {/* Career Exploration */}
            <div className="bg-[#E3F2FD] rounded-3xl p-6 sm:p-8 border-2 border-blue-200 hover:shadow-2xl hover:scale-[1.02] transition-all cursor-pointer text-center">
              <div className="w-32 h-32 sm:w-40 sm:h-40 mx-auto mb-4 sm:mb-6">
                <LazyLottie src="/animations/mascot-curious.json" loop={true} autoplay={true} className="w-full h-full" style={{ mixBlendMode: 'multiply' }} />
              </div>
              <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-2">Career Exploration</h3>
              <p className="text-sm font-bold text-blue-700 mb-3">
                Find your direction before everyone else does.
              </p>
              <p className="text-gray-700 text-sm sm:text-base mb-4 sm:mb-6">
                Discover careers that match your strengths
              </p>
              <Link href="/discover" aria-label="Learn more about Career Exploration">
                <Button className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm sm:text-base py-3">
                  Learn More
                </Button>
              </Link>
            </div>

            {/* Exam Prep */}
            <div className="bg-[#F3E5F5] rounded-3xl p-6 sm:p-8 border-2 border-purple-200 hover:shadow-2xl hover:scale-[1.02] transition-all cursor-pointer text-center">
              <div className="w-32 h-32 sm:w-40 sm:h-40 mx-auto mb-4 sm:mb-6">
                <LazyLottie src="/animations/mascot-studying.json" loop={true} autoplay={true} className="w-full h-full" style={{ mixBlendMode: 'multiply' }} />
              </div>
              <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-2">Exam Prep</h3>
              <p className="text-sm font-bold text-purple-700 mb-3">
                7s aren&apos;t luck. They&apos;re a system.
              </p>
              <p className="text-gray-700 text-sm sm:text-base mb-4 sm:mb-6">
                Master IB with AI tutoring & practice
              </p>
              <Link href="/ib-learning" aria-label="Learn more about Exam Prep">
                <Button className="w-full rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm sm:text-base py-3">
                  Learn More
                </Button>
              </Link>
            </div>

            {/* Profile Building (renamed from Passion Projects) */}
            <div className="bg-[#FFF3E0] rounded-3xl p-6 sm:p-8 border-2 border-orange-200 hover:shadow-2xl hover:scale-[1.02] transition-all cursor-pointer text-center">
              <div className="w-32 h-32 sm:w-40 sm:h-40 mx-auto mb-4 sm:mb-6">
                <LazyLottie src="/animations/mascot-building.json" loop={true} autoplay={true} className="w-full h-full" style={{ mixBlendMode: 'multiply' }} />
              </div>
              <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-2">Profile Building</h3>
              <p className="text-sm font-bold text-orange-700 mb-3">
                Build the story that gets you in.
              </p>
              <p className="text-gray-700 text-sm sm:text-base mb-4 sm:mb-6">
                Create projects that stand out
              </p>
              <Link href="/projects" aria-label="Learn more about Profile Building">
                <Button className="w-full rounded-xl bg-orange-700 hover:bg-orange-800 text-white font-bold text-sm sm:text-base py-3">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>


      {/* Our Methodology - Simplified 4 Pillars */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-3 sm:mb-4">
              Our Methodology
            </h2>
            <p className="text-lg sm:text-xl text-gray-600">Built on 4 pillars of student success</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-6xl mx-auto">
            {/* Joy */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6 border-2 border-green-200 hover:shadow-xl hover:scale-[1.02] transition-all text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 mx-auto mb-3 sm:mb-4">
                <LazyLottie src="/animations/happymascot.json" loop={true} autoplay={true} className="w-full h-full" style={{ mixBlendMode: 'multiply' }} />
              </div>
              <h3 className="text-lg sm:text-xl font-black text-gray-900 mb-2">Joy</h3>
              <p className="text-gray-600 text-xs sm:text-sm">
                Learning should be enjoyable, not stressful
              </p>
            </div>

            {/* Outcomes */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6 border-2 border-blue-200 hover:shadow-xl hover:scale-[1.02] transition-all text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 mx-auto mb-3 sm:mb-4">
                <LazyLottie src="/animations/mascot-victorious.json" loop={true} autoplay={true} className="w-full h-full" style={{ mixBlendMode: 'multiply' }} />
              </div>
              <h3 className="text-lg sm:text-xl font-black text-gray-900 mb-2">Outcomes</h3>
              <p className="text-gray-600 text-xs sm:text-sm">
                Real results that boost your IB performance
              </p>
            </div>

            {/* Journey */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6 border-2 border-purple-200 hover:shadow-xl hover:scale-[1.02] transition-all text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 mx-auto mb-3 sm:mb-4">
                <LazyLottie src="/animations/mascot-walking.json" loop={true} autoplay={true} className="w-full h-full" style={{ mixBlendMode: 'multiply' }} />
              </div>
              <h3 className="text-lg sm:text-xl font-black text-gray-900 mb-2">Journey</h3>
              <p className="text-gray-600 text-xs sm:text-sm">
                Progress through gamification and rewards
              </p>
            </div>

            {/* Opportunity */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6 border-2 border-orange-200 hover:shadow-xl hover:scale-[1.02] transition-all text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 mx-auto mb-3 sm:mb-4">
                <LazyLottie src="/animations/mascot-idea.json" loop={true} autoplay={true} className="w-full h-full" style={{ mixBlendMode: 'multiply' }} />
              </div>
              <h3 className="text-lg sm:text-xl font-black text-gray-900 mb-2">Opportunity</h3>
              <p className="text-gray-600 text-xs sm:text-sm">
                Access world-class resources anytime
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Mobile Version (simple linear layout) */}
      <section id="how-it-works" className="md:hidden py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-3">
              How it works
            </h2>
            <p className="text-lg text-gray-600">Start your journey in 3 simple steps</p>
          </div>

          <div className="space-y-4 max-w-md mx-auto">
            {/* Step 1 - Sign Up */}
            <div className="relative bg-white rounded-2xl p-5 border-3 border-green-300 shadow-lg overflow-hidden">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 flex-shrink-0">
                  <LazyLottie src="/animations/mascot-egg.json" loop={true} autoplay={true} className="w-full h-full" style={{ mixBlendMode: 'multiply' }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-6 h-6 rounded-full bg-green-500 text-white text-xs font-bold flex items-center justify-center">1</span>
                    <h3 className="text-lg font-black text-gray-900">Sign Up</h3>
                  </div>
                  <p className="text-gray-600 text-sm">Free in &lt;60s</p>
                  <span className="inline-block mt-2 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Free trial</span>
                </div>
              </div>
            </div>

            {/* Step 2 - We Build Your Roadmap */}
            <div className="relative bg-white rounded-2xl p-5 border-3 border-blue-300 shadow-lg overflow-hidden">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 flex-shrink-0">
                  <LazyLottie src="/animations/mascot-pointing.json" loop={true} autoplay={true} className="w-full h-full" style={{ mixBlendMode: 'multiply' }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">2</span>
                    <h3 className="text-lg font-black text-gray-900">We build your personalised roadmap</h3>
                  </div>
                  <p className="text-gray-600 text-sm">Tell us your goals and we&apos;ll map out exactly where to start</p>
                  <span className="inline-block mt-2 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">Tailored for you</span>
                </div>
              </div>
            </div>

            {/* Step 3 - Start Learning */}
            <div className="relative bg-white rounded-2xl p-5 border-3 border-purple-300 shadow-lg overflow-hidden">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 flex-shrink-0">
                  <LazyLottie src="/animations/mascot-celebrating.json" loop={true} autoplay={true} className="w-full h-full" style={{ mixBlendMode: 'multiply' }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-6 h-6 rounded-full bg-purple-500 text-white text-xs font-bold flex items-center justify-center">3</span>
                    <h3 className="text-lg font-black text-gray-900">Start Learning</h3>
                  </div>
                  <p className="text-gray-600 text-sm">Earn XP & level up</p>
                  <span className="inline-block mt-2 px-2.5 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">Gamified rewards</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Desktop Scrollytelling (hidden on mobile) */}
      <section id="how-it-works" ref={howItWorksRef} className="relative bg-white hidden md:block">
        <div className="sticky top-0 h-screen flex items-center overflow-hidden">
          {/* Background decoration */}
          <div className={`absolute inset-0 transition-all duration-700 ${
            activeStep === 1 ? 'bg-gradient-to-b from-green-50/30 to-green-50/10' :
            activeStep === 2 ? 'bg-gradient-to-b from-blue-50/30 to-blue-50/10' :
            'bg-gradient-to-b from-purple-50/30 to-purple-50/10'
          }`} />

          <div className="container mx-auto px-4 relative z-10">
            <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              {/* Left side - Dynamic visual with mascot */}
              <div className="relative">
                <div className="text-center">
                  {/* Animated mascot replacing emoji */}
                  <div className={`w-120 h-120 mx-auto mb-8 transition-all duration-500 transform hover:scale-110 ${
                    // activeStep === 1 ? 'animate-bounce' :
                    // activeStep === 2 ? 'animate-pulse' :
                    ''
                  }`}>
                    <LazyLottie
                      src={activeStep === 1 ? '/animations/mascot-egg.json' : activeStep === 2 ? '/animations/mascot-pointing.json' : '/animations/mascot-celebrating.json'}
                      loop={true}
                      autoplay={true}
                      className="w-full h-full"
                      style={{ mixBlendMode: 'multiply' }}
                    />
                  </div>
                  <div className="space-y-4">
                    <div className={`h-2 rounded-full transition-all duration-700 ${
                      activeStep === 1 ? 'w-full bg-gradient-to-r from-green-400 to-green-600' :
                      activeStep === 2 ? 'w-full bg-gradient-to-r from-blue-400 to-blue-600' :
                      'w-full bg-gradient-to-r from-purple-400 to-purple-600'
                    }`}></div>
                    <div className={`mx-auto h-2 rounded-full transition-all duration-700 ${
                      activeStep >= 2 ? 'w-4/5 bg-gradient-to-r from-blue-400 to-blue-600' : 'w-4/5 bg-gray-200'
                    }`}></div>
                    <div className={`mx-auto h-2 rounded-full transition-all duration-700 ${
                      activeStep >= 3 ? 'w-3/5 bg-gradient-to-r from-purple-400 to-purple-600' : 'w-3/5 bg-gray-200'
                    }`}></div>
                  </div>
                </div>
              </div>

              {/* Right side - Dynamic content */}
              <div>
                <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
                  How it works
                </h2>
                <p className="text-xl text-gray-600 mb-8">Start achieving your goals in 3 simple steps</p>

                {/* Step indicators */}
                <div className="space-y-6">
                  {/* Step 1 */}
                  <div className={`bg-white rounded-3xl p-6 border-4 shadow-xl transition-all duration-700 ${
                    activeStep === 1
                      ? 'border-green-400 shadow-green-200/50 opacity-100 scale-105'
                      : 'border-green-200 opacity-60 scale-100'
                  }`}>
                    <div className="flex items-center gap-4 mb-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-black text-lg transition-all duration-700 ${
                        activeStep === 1
                          ? 'bg-gradient-to-br from-green-500 to-green-600 scale-110'
                          : 'bg-gradient-to-br from-green-400 to-green-500'
                      }`}>
                        1
                      </div>
                      <h3 className="text-xl font-black text-gray-900">Sign Up</h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed mb-3">
                      Free in &lt;60s - Start your free trial today.
                    </p>
                    {activeStep === 1 && (
                      <div className="flex flex-wrap gap-2 animate-fadeIn">
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">No credit card required</span>
                        
                      </div>
                    )}
                  </div>

                  {/* Step 2 */}
                  <div className={`bg-white rounded-3xl p-6 border-4 shadow-xl transition-all duration-700 ${
                    activeStep === 2
                      ? 'border-blue-400 shadow-blue-200/50 opacity-100 scale-105'
                      : 'border-blue-200 opacity-60 scale-100'
                  }`}>
                    <div className="flex items-center gap-4 mb-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-black text-lg transition-all duration-700 ${
                        activeStep === 2
                          ? 'bg-gradient-to-br from-blue-500 to-blue-600 scale-110'
                          : 'bg-gradient-to-br from-blue-400 to-blue-500'
                      }`}>
                        2
                      </div>
                      <h3 className="text-xl font-black text-gray-900">We build your personalised roadmap</h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      Tell us your goals and we&apos;ll map out exactly where to start.
                    </p>
                    {activeStep === 2 && (
                      <div className="flex flex-wrap gap-2 mt-3 animate-fadeIn">
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">Tailored for you</span>
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">AI-powered</span>
                      </div>
                    )}
                  </div>

                  {/* Step 3 */}
                  <div className={`bg-white rounded-3xl p-6 border-4 shadow-xl transition-all duration-700 ${
                    activeStep === 3
                      ? 'border-purple-400 shadow-purple-200/50 opacity-100 scale-105'
                      : 'border-purple-200 opacity-60 scale-100'
                  }`}>
                    <div className="flex items-center gap-4 mb-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-black text-lg transition-all duration-700 ${
                        activeStep === 3
                          ? 'bg-gradient-to-br from-purple-500 to-purple-600 scale-110'
                          : 'bg-gradient-to-br from-purple-400 to-purple-500'
                      }`}>
                        3
                      </div>
                      <h3 className="text-xl font-black text-gray-900">Start Learning</h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      Earn XP & level up with gamified learning.
                    </p>
                    {activeStep === 3 && (
                      <div className="flex flex-wrap gap-2 mt-3 animate-fadeIn">
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">Streaks & badges</span>
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">Track progress</span>
                      </div>
                    )}
                  </div>
                </div>

                <p className="mt-8 text-sm text-gray-500 italic">
                  {activeStep === 1 && '👇 Scroll down to see step 2'}
                  {activeStep === 2 && '👇 Keep scrolling to see step 3'}
                  {activeStep === 3 && '✨ You\'ve completed all steps!'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll spacers for each step */}
        <div className="h-screen"></div>
        <div className="h-screen"></div>
      </section>

      {/* All Features - Revision Dojo Style */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-3 sm:mb-4">
              All Features
            </h2>
            <p className="text-lg sm:text-xl text-gray-600">Everything you need to succeed</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 max-w-7xl mx-auto">
            {/* Gamified Learning */}
            <div className="group bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border-2 border-gray-200 hover:border-green-300 hover:shadow-2xl transition-all cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4 md:mb-6 group-hover:scale-110 transition-transform">
                <div className="text-2xl sm:text-3xl md:text-4xl">🎮</div>
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-black text-gray-900 mb-1.5 sm:mb-2 md:mb-3">Gamified Learning</h3>
              <p className="text-gray-600 leading-relaxed text-xs sm:text-sm md:text-base">
                Earn XP, build streaks, and unlock achievements as you learn.
              </p>
            </div>

            {/* AI Chat */}
            <div className="group bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border-2 border-gray-200 hover:border-purple-300 hover:shadow-2xl transition-all cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4 md:mb-6 group-hover:scale-110 transition-transform overflow-hidden">
                <LazyLottie
                  src="/animations/animation.json"
                  loop={true}
                  autoplay={true}
                  className="w-full h-full"
                  style={{ mixBlendMode: 'multiply' }}
                />
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-black text-gray-900 mb-1.5 sm:mb-2 md:mb-3">InnoG AI</h3>
              <p className="text-gray-600 leading-relaxed text-xs sm:text-sm md:text-base">
                AI tutor available 24/7 to help you understand any concept.
              </p>
            </div>

            {/* Question Bank */}
            <div className="group bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border-2 border-gray-200 hover:border-blue-300 hover:shadow-2xl transition-all cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4 md:mb-6 group-hover:scale-110 transition-transform">
                <div className="text-2xl sm:text-3xl md:text-4xl">📚</div>
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-black text-gray-900 mb-1.5 sm:mb-2 md:mb-3">Question Bank</h3>
              <p className="text-gray-600 leading-relaxed text-xs sm:text-sm md:text-base">
                Thousands of real exam questions that actually prepare you.
              </p>
            </div>

            {/* Progress Tracking */}
            <div className="group bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border-2 border-gray-200 hover:border-green-300 hover:shadow-2xl transition-all cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4 md:mb-6 group-hover:scale-110 transition-transform">
                <div className="text-2xl sm:text-3xl md:text-4xl">📊</div>
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-black text-gray-900 mb-1.5 sm:mb-2 md:mb-3">Track Progress</h3>
              <p className="text-gray-600 leading-relaxed text-xs sm:text-sm md:text-base">
                Visual dashboards show your growth across all pathways.
              </p>
            </div>

            {/* Mock Exams */}
            <div className="group bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border-2 border-gray-200 hover:border-purple-300 hover:shadow-2xl transition-all cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4 md:mb-6 group-hover:scale-110 transition-transform">
                <div className="text-2xl sm:text-3xl md:text-4xl">📝</div>
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-black text-gray-900 mb-1.5 sm:mb-2 md:mb-3">Mock Exams</h3>
              <p className="text-gray-600 leading-relaxed text-xs sm:text-sm md:text-base">
                Custom exams that target your weak spots.
              </p>
            </div>

            {/* Career Tools */}
            <div className="group bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border-2 border-gray-200 hover:border-blue-300 hover:shadow-2xl transition-all cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4 md:mb-6 group-hover:scale-110 transition-transform">
                <div className="text-2xl sm:text-3xl md:text-4xl">🎯</div>
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-black text-gray-900 mb-1.5 sm:mb-2 md:mb-3">Career Discovery</h3>
              <p className="text-gray-600 leading-relaxed text-xs sm:text-sm md:text-base">
                Self-discovery tools and career matching AI.
              </p>
            </div>

            {/* Profile Builder (renamed from Project Builder) */}
            <div className="group bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border-2 border-gray-200 hover:border-orange-300 hover:shadow-2xl transition-all cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4 md:mb-6 group-hover:scale-110 transition-transform">
                <div className="text-2xl sm:text-3xl md:text-4xl">🚀</div>
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-black text-gray-900 mb-1.5 sm:mb-2 md:mb-3">Profile Builder</h3>
              <p className="text-gray-600 leading-relaxed text-xs sm:text-sm md:text-base">
                Build projects and experiences that stand out.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* Testimonials Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-green-600 via-blue-600 to-purple-700">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-3">
              Loved by Students Worldwide
            </h2>
            <p className="text-green-100 text-lg sm:text-xl">
              Real stories from students who transformed their academic journey
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Testimonial 1 */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <div className="flex mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <blockquote className="text-white mb-4 text-sm sm:text-base leading-relaxed">
                &quot;InnOGyan helped me go from a predicted 32 to 42 in IB. The AI tutor explains concepts better than my actual teachers!&quot;
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-600 flex-shrink-0" />
                <div>
                  <div className="font-bold text-white text-sm">Emma Chen</div>
                  <div className="text-green-100 text-xs">Stanford • IB: 43</div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <div className="flex mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <blockquote className="text-white mb-4 text-sm sm:text-base leading-relaxed">
                &quot;The career exploration module helped me discover my passion for biotechnology. Now I have a clear path forward!&quot;
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 flex-shrink-0" />
                <div>
                  <div className="font-bold text-white text-sm">Arjun Patel</div>
                  <div className="text-green-100 text-xs">Imperial College • IB: 41</div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <div className="flex mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <blockquote className="text-white mb-4 text-sm sm:text-base leading-relaxed">
                &quot;Practice questions are exam-level perfect. Got 7s in Math AA HL and Physics HL thanks to the AI explanations!&quot;
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-green-400 to-teal-600 flex-shrink-0" />
                <div>
                  <div className="font-bold text-white text-sm">Sofia Rodriguez</div>
                  <div className="text-green-100 text-xs">MIT • IB: 44</div>
                </div>
              </div>
            </div>

            {/* Testimonial 4 */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <div className="flex mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <blockquote className="text-white mb-4 text-sm sm:text-base leading-relaxed">
                &quot;Built my passion project on climate tech with InnoG. It became my Common App essay and got me into Berkeley!&quot;
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-pink-400 to-rose-600 flex-shrink-0" />
                <div>
                  <div className="font-bold text-white text-sm">Yuki Tanaka</div>
                  <div className="text-green-100 text-xs">UC Berkeley • IB: 42</div>
                </div>
              </div>
            </div>

            {/* Testimonial 5 */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <div className="flex mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <blockquote className="text-white mb-4 text-sm sm:text-base leading-relaxed">
                &quot;As an IGCSE student, I felt lost about careers. InnoG&apos;s assessments gave me clarity and confidence in my future path.&quot;
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-400 to-blue-600 flex-shrink-0" />
                <div>
                  <div className="font-bold text-white text-sm">Marcus Johnson</div>
                  <div className="text-green-100 text-xs">Oxford • IGCSE: 9A*</div>
                </div>
              </div>
            </div>

            {/* Testimonial 6 */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <div className="flex mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <blockquote className="text-white mb-4 text-sm sm:text-base leading-relaxed">
                &quot;The daily streak system kept me consistent. Went from procrastination to studying 2 hours daily. Game changer!&quot;
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-600 flex-shrink-0" />
                <div>
                  <div className="font-bold text-white text-sm">Priya Sharma</div>
                  <div className="text-green-100 text-xs">Cambridge • A-Level: 4A*</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Schools Banner */}
      <section className="py-12 sm:py-16 bg-gradient-to-r from-green-50 to-blue-50 border-y-2 border-green-200">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 mb-3">
              Bringing InnOGyan to your school?
            </h2>
            <p className="text-base sm:text-lg text-gray-700 mb-6">
              We partner with IB, IGCSE and A-Level schools across Asia, the Middle East and Europe.
            </p>
            <a href="mailto:partnerships@innogyan.com">
              <Button
                size="lg"
                variant="outline"
                className="rounded-xl border-2 border-green-600 text-green-700 hover:bg-green-50 font-bold px-8 py-4"
              >
                Talk to us about school partnerships →
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA - Parent-focused */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-5 sm:space-y-8">
            {/* Encouraging mascot */}
            <div className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 mx-auto">
              <LazyLottie src="/animations/mascot-waving.json" loop={true} autoplay={true} className="w-full h-full" style={{ mixBlendMode: 'multiply' }} />
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-gray-900">
              See what InnOGyan can do for your child.
            </h2>
            <p className="text-lg sm:text-xl text-gray-600">
              Join students from 15+ countries already learning with AI + mentor support.
            </p>

            {/* CTA */}
            <div className="flex justify-center">
              <Link href="/sign-up">
                <Button
                  size="lg"
                  className="rounded-xl sm:rounded-2xl bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold text-base sm:text-lg md:text-xl px-6 sm:px-8 md:px-12 py-4 sm:py-6 md:py-8 shadow-2xl hover:shadow-3xl transition-all hover:scale-105"
                >
                  Start Your Trial
                </Button>
              </Link>
            </div>

            <p className="text-xs sm:text-sm text-gray-500">Free trial available. No credit card required.</p>
          </div>
        </div>
      </section>

      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-8">
            <div className="col-span-2 sm:col-span-2 md:col-span-1">
              <Logo
                size="lg"
                textClassName="text-lg sm:text-xl font-bold text-white"
              />
              <p className="text-gray-300 text-xs sm:text-sm mt-3 sm:mt-4">Be the OG - All-in-one platform for student success. Career exploration, exam prep, and profile building.</p>
            </div>
            <div>
              <h3 className="font-bold mb-3 sm:mb-4 text-sm sm:text-base">Features</h3>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-300">
                <li><a href="#" className="block py-1.5 hover:text-white">Career Exploration</a></li>
                <li><a href="#" className="block py-1.5 hover:text-white">Exam Prep</a></li>
                <li><a href="#" className="block py-1.5 hover:text-white">Profile Building</a></li>
                <li><a href="#" className="block py-1.5 hover:text-white">AI Tutor</a></li>
                <li><a href="/pricing" className="block py-1.5 hover:text-white">Pricing</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-3 sm:mb-4 text-sm sm:text-base">Resources</h3>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-300">
                <li><a href="#" className="block py-1.5 hover:text-white">Help Center</a></li>
                <li><a href="#" className="block py-1.5 hover:text-white">Blog</a></li>
                <li><a href="#" className="block py-1.5 hover:text-white">Success Stories</a></li>
                <li><a href="#" className="block py-1.5 hover:text-white">For Teachers</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-3 sm:mb-4 text-sm sm:text-base">Company</h3>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-300">
                <li><a href="#" className="block py-1.5 hover:text-white">About Us</a></li>
                <li><a href="#" className="block py-1.5 hover:text-white">Contact</a></li>
                <li><a href="#" className="block py-1.5 hover:text-white">Privacy Policy</a></li>
                <li><a href="#" className="block py-1.5 hover:text-white">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-6 sm:pt-8 text-center text-xs sm:text-sm text-gray-300">
            © 2026 InnOGyan. Be the OG. Empowering students worldwide in 15+ countries.
          </div>
        </div>
      </footer>
    </div>
  );
}
